import React, { useCallback, useState } from "react";
import {
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Link,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { confirm } from "material-ui-confirm";
import axiosClient from "../../../../../../../lib/axios";
import BtnLoading from "../../../../../../../components/BasicComponents/Buttons/BtnLoading";

export default function DialogCancelarOC({
    open,
    onClose,
    numeroOC,
    solicitacaoCodigo,
}: DialogCancelarOCProps) {

    const [motivo, setMotivo] = useState("");
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { mutate: cancelarOrdemCompra, isLoading } = useMutation({
        mutationKey: ["cancelar-ordem-compra"],
        mutationFn: async (data: { numero_ordem_compra: string; motivo_cancelamento: string, redirecionar: boolean }) => {
            return await axiosClient.post("/estoque/compras/cancelar-ordem-compra", data);
        },
        onSuccess: (data, variables) => {
            enqueueSnackbar("Ordem de compra cancelada com sucesso!", { variant: "success" });
            queryClient.invalidateQueries(["detalhe-ordem-compra"]);
            queryClient.invalidateQueries(["ordens-compra"]);
            setMotivo("");
            if (variables.redirecionar) {
                navigate(`/estoque/compras/listar-solicitacoes?detalhe-solicitacao=${solicitacaoCodigo}`);
            }
            onClose();
        },
        onError: (error: any) => {
            console.error("Erro ao cancelar ordem de compra:", error);
            enqueueSnackbar("Erro ao cancelar ordem de compra", { variant: "error" });
        }
    });

    const handleConfirmar = useCallback(
        async () => {
            confirm({ description: 'O cancelamento da cotação resultará em itens solicitados sem ordem de compra, deseja gerar uma nova ordem de compra para os itens cancelados?' })
                .then(() => {
                    if (!motivo.trim()) {
                        enqueueSnackbar("Por favor, informe o motivo do cancelamento", { variant: "warning" });
                        return;
                    }
                    cancelarOrdemCompra({
                        numero_ordem_compra: numeroOC,
                        motivo_cancelamento: motivo.trim(),
                        redirecionar: true
                    });

                })
                .catch(() => {
                    if (!motivo.trim()) {
                        enqueueSnackbar("Por favor, informe o motivo do cancelamento", { variant: "warning" });
                        return;
                    }
                    cancelarOrdemCompra({
                        numero_ordem_compra: numeroOC,
                        motivo_cancelamento: motivo.trim(),
                        redirecionar: false
                    });
                });
        },
        [motivo, numeroOC, cancelarOrdemCompra]
    );

    const handleFechar = () => {
        setMotivo("");
        onClose();
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle fontWeight="bold">Cancelar Ordem de compra</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 3 }}>
                    Será cancelada a ordem de compra <Link>{numeroOC}</Link>, deseja continuar?
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="tipo-cancelamento-label">Tipo de Cancelamento</InputLabel>
                    <Select
                        labelId="tipo-cancelamento-label"
                        value={motivo}
                        label="Tipo de Cancelamento"
                        onChange={(e) => setMotivo(e.target.value)}
                        disabled={isLoading}
                    >
                        <MenuItem value="Divergência Acatada">Divergência Acatada</MenuItem>
                        <MenuItem value="Devolução por desacordo">Devolução por desacordo</MenuItem>
                        <MenuItem value="Acerto com fornecedor">Acerto com fornecedor</MenuItem>
                        <MenuItem value="Divergência Financeira">Divergência Financeira</MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleFechar}
                    color="error"
                    variant="text"
                    disabled={isLoading}
                >
                    Cancelar
                </Button>
                <BtnLoading
                    onClick={() => handleConfirmar()}
                    color="error"
                    variant="contained"
                    loading={isLoading}
                    loadingText="Cancelando..."
                    disabled={!motivo.trim()}
                >
                    Confirmar
                </BtnLoading>
            </DialogActions>
        </Dialog>
    );
}

export interface DialogCancelarOCProps {
    open: boolean;
    onClose: () => void;
    numeroOC: string;
    solicitacaoCodigo: string;
}